import { Article, BreadcrumbList, ListItem, Product, WithContext } from "schema-dts";

import { homepageUrl } from "@/mk2/apps/home/urls";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { isCategoryArticle, wikiArticleUrl, wikiCategoryUrl } from "@/mk2/apps/wiki/utils";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import {
    getCoverOrFirstPhoto,
    getPhotoResizedDimensions,
    getUrlOfCoverImageOrFirstImageOrFallback,
    organizationModrykonikWithoutContext,
} from "@/mk2/helpers/structuredData";
import { YMDHMSIso } from "@/mk2/helpers/timeformat";
import { fbMorykonikProfileUrl } from "@/mk2/helpers/urls";
import {
    ArticleConceptPath,
    WikiArticleEntity,
    WikiCategoryEntity,
    WikiEntityEntity,
} from "@/mk2/schemas";
import { photoUrl } from "@/mk/photo/photoUrl";
import { BASE_URL } from "@/mk/settings";

const breadcrumbCategory = (baseUrl: string, category: WikiCategoryEntity): ListItem => ({
    "@type": "ListItem",
    position: 1,
    item: {
        "@id": baseUrl + wikiCategoryUrl(category),
        name: category.name,
    },
});

const breadcrumbArticle = (baseUrl: string, article: WikiArticleEntity): ListItem => ({
    "@type": "ListItem",
    position: 2,
    item: {
        "@id": baseUrl + wikiArticleUrl(article),
        name: article.title,
    },
});

export function breadcrumbsWiki(baseUrl, article: WikiArticleEntity): WithContext<BreadcrumbList> {
    const items = [];

    items.push(breadcrumbCategory(baseUrl, article.category));

    if (!isCategoryArticle(article)) {
        items.push(breadcrumbArticle(baseUrl, article));
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function conceptsBreadcrumbsWiki(
    baseUrl,
    articleConceptPath: ArticleConceptPath[],
): WithContext<BreadcrumbList> {
    const items: ListItem[] = articleConceptPath.map((concept, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        item: {
            "@id": baseUrl + getWikiArticleUrl(concept.categorySlug, concept.articleSlug),
            name: concept.title,
        },
    }));

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function reviewArticleWiki(baseUrl: string, reviewArticle: WikiArticleEntity) {
    const reviewArticleUrl = wikiArticleUrl(reviewArticle);

    // according to https://developers.google.com/search/docs/data-types/article#amp
    const result: WithContext<Product> = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": baseUrl + reviewArticleUrl + "#Product",
        name: reviewArticle.title,
        image: getUrlOfCoverImageOrFirstImageOrFallback(
            reviewArticle.bodyAST,
            reviewArticle.photos,
        ),
        description: getFirstParagraphsPlaintext(reviewArticle.bodyAST),
        sku: reviewArticle.id.toString(),
        url: baseUrl + reviewArticleUrl,
        ...(reviewArticle.gtin && { gtin: reviewArticle.gtin }),
    };
    const brandLink = reviewArticle.entityLinks?.find((link) => link.role === "brand");
    if (brandLink) {
        (result as any).brand = entityToSchemaOrg(brandLink.entity);
    }

    const linksWithRole = (role: string) =>
        (reviewArticle.entityLinks ?? [])
            .filter((l) => l.role === role)
            .map((l) => entityToSchemaOrg(l.entity));

    const authorLinks = linksWithRole("author");
    const reviewerLinks = linksWithRole("reviewer");
    const contributorLinks = linksWithRole("contributor");

    if (authorLinks.length) (result as any).author = authorLinks;
    if (contributorLinks.length) (result as any).contributor = contributorLinks;
    if (reviewerLinks.length) (result as any).reviewedBy = reviewerLinks;
    Object.assign(result, experienceReviewData(reviewArticle));
    return result;
}

// Builds the schema.org `review` array + `aggregateRating` from an article's user
// experiences (skusenosti). Both are valid on Product and on CreativeWork/Article,
// so it is shared by reviewArticleWiki() and articleWiki().
function experienceReviewData(article: WikiArticleEntity): {
    review?: object[];
    aggregateRating?: object;
} {
    // Ratings only make sense when the article allows starred experiences.
    if (!article.experiencesStarsAllowed || !article.experiences?.length) return {};

    const result: { review?: object[]; aggregateRating?: object } = {
        review: article.experiences
            .filter((experience) => experience.starsCount > 0)
            .map((experience) => ({
                // No nested @context here: these Review objects are embedded inside the
                // parent Article/Product node. A nested @context makes Google's parser
                // treat each review as a separate root node, producing the
                // "Invalid object type for field '<parent_node>'" rich-results error.
                "@type": "Review",
                author: {
                    "@type": "Person",
                    name: experience.authorUser.username,
                },
                datePublished: experience.created,
                reviewBody: experience.text,
                reviewRating: {
                    "@type": "Rating",
                    bestRating: 5,
                    worstRating: 1,
                    ratingValue: experience.starsCount,
                },
            })),
    };

    if (article.experiencesCount > 0 && (article.experiencesScore ?? false) !== false) {
        result.aggregateRating = {
            "@type": "AggregateRating",
            bestRating: 5,
            worstRating: 1,
            ratingValue: article.experiencesScore,
            reviewCount: article.experiencesCount,
            ratingCount: article.experiencesCount,
        };
    }

    return result;
}

const entityTypeToSchemaType: Record<string, string> = {
    brand: "Brand",
    organization: "Organization",
    medical_organization: "MedicalOrganization",
    medical_business: "MedicalBusiness",
    local_business: "LocalBusiness",
    service: "Service",
    person: "Person",
};

function entityToSchemaOrg(entity: WikiEntityEntity) {
    const sameAs = [...(entity.sameAsUrls || [])];
    if (entity.wikidataQid) sameAs.push(`https://www.wikidata.org/wiki/${entity.wikidataQid}`);
    return {
        "@type": entityTypeToSchemaType[entity.entityType] ?? "Organization",
        name: entity.name,
        ...(entity.url && { url: entity.url }),
        ...(entity.description && { description: entity.description }),
        ...(sameAs.length && { sameAs }),
    };
}

export function articleWiki(baseUrl: string, article: WikiArticleEntity): WithContext<Article> {
    const createdOn: string = YMDHMSIso(article.createdAt);
    const modifiedOn: string = YMDHMSIso(article.modifiedAt);
    const articleUrl = wikiArticleUrl(article);

    const linksWithRole = (role: string) =>
        (article.entityLinks ?? [])
            .filter((l) => l.role === role)
            .map((l) => entityToSchemaOrg(l.entity));

    const authorLinks = linksWithRole("author");
    const reviewerLinks = linksWithRole("reviewer");
    const contributorLinks = linksWithRole("contributor");

    // according to https://developers.google.com/search/docs/data-types/article#amp
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": baseUrl + articleUrl + "#article",
        headline: article.title,
        description: getFirstParagraphsPlaintext(article.bodyAST),
        image: getUrlOfCoverImageOrFirstImageOrFallback(article.bodyAST, article.photos),
        datePublished: createdOn,
        dateModified: modifiedOn ? modifiedOn : createdOn,
        author: authorLinks.length ? authorLinks : organizationModrykonikWithoutContext(false),
        ...(contributorLinks.length && { contributor: contributorLinks }),
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": baseUrl + articleUrl,
            ...(reviewerLinks.length && { reviewedBy: reviewerLinks }),
        },
        publisher: organizationModrykonikWithoutContext(false),
        // NOTE: review[] / aggregateRating intentionally NOT emitted on Article.
        // Google does not support review rich results for Article, so the markup is
        // ignored at best and (when malformed) triggers rich-results errors. To surface
        // star ratings for these pages (e.g. porodnica / institutions), model the subject
        // as a dedicated Product/LocalBusiness node carrying review + aggregateRating,
        // instead of hanging them off the Article. See experienceReviewData().
        // ...experienceReviewData(article),
    } as WithContext<Article>;
}

// Roles embedded inline in the Article/WebPage block — skip as standalone entities to avoid duplication
const INLINE_ROLES = new Set(["author", "reviewer", "contributor"]);

export function entitiesForArticle(article: WikiArticleEntity): object[] {
    if (!article.entityLinks?.length) return [];

    return article.entityLinks
        .filter((link) => !INLINE_ROLES.has(link.role))
        .map((link) => ({
            "@context": "https://schema.org",
            ...entityToSchemaOrg(link.entity),
        }));
}

export function videoObjectsForArticle(article: WikiArticleEntity): object[] {
    if (!article.videoLinks?.length) return [];
    return article.videoLinks.map((link) => ({
        "@context": "https://schema.org",
        ...link.video.jsonld,
    }));
}

export function articleWikiOG(baseUrl: string, article: WikiArticleEntity) {
    const createdOn: string = YMDHMSIso(article.createdAt);
    const modifiedOn: string = YMDHMSIso(article.modifiedAt);
    const articleUrl = wikiArticleUrl(article);
    const mainPhoto = article.coverPhoto || getCoverOrFirstPhoto(article.bodyAST, article.photos);
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    // according to https://developers.facebook.com/docs/reference/opengraph/object-type/article/
    return {
        "og:type": "article",
        "og:title": article.title,
        "og:description": getFirstParagraphsPlaintext(article.bodyAST),
        "og:image": article.coverPhoto
            ? photoUrl(article.coverPhoto, "s1600x1600")
            : getUrlOfCoverImageOrFirstImageOrFallback(article.bodyAST, article.photos),
        "og:image:alt": article.title,
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
        "og:url": baseUrl + articleUrl,

        "article:author": fbMorykonikProfileUrl(),
        "article:content_tier": "free",
        "article:published_time": createdOn,
        "article:modified_time": modifiedOn ? modifiedOn : createdOn,
        "article:publisher": BASE_URL + homepageUrl,
    };
}
